import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isAppPath, isFramedPath } from '../lib/embedRoute';
import {
  BRAND_CURSOR_EVENT,
  brandCursorSupported,
  isBrandCursorDisabled,
} from '../lib/brandCursor';

// Cursor animado de InvokeApp en toda la web. Los ficheros viven en
// public/assets/invokeapp-cursor/ (lottie-web 5.13.0 ligero + controlador + JSON);
// no llaman a ningún servicio externo. Se monta UNA vez en el cliente y el
// controlador pinta un overlay con pointer-events:none: los clics llegan a la web.
// Si algo falla (descarga, montaje), se queda el cursor del sistema.
const BASE = '/assets/invokeapp-cursor/';
const VERSION = '1';

// Rutas sin cursor de marca: las demos con la marca de un cliente y las que viven
// dentro del <iframe> de otra web o de CORE-Front.
const isBrandless = (pathname) => pathname.startsWith('/playbook-demo') || isFramedPath(pathname) || isAppPath(pathname);

const scriptOnce = (src, ready) =>
  new Promise((resolve, reject) => {
    if (ready()) return resolve();
    let el = document.querySelector(`script[data-invokeapp-cursor="${src}"]`);
    if (!el) {
      el = document.createElement('script');
      el.src = `${BASE}${src}?v=${VERSION}`;
      el.async = true;
      el.dataset.invokeappCursor = src;
      document.head.appendChild(el);
    }
    el.addEventListener('load', () => (ready() ? resolve() : reject(new Error(src))), { once: true });
    el.addEventListener('error', () => reject(new Error(src)), { once: true });
  });

let mounting = null;

const mountCursor = () => {
  if (window.brandCursor && !window.brandCursor.destroyed) return Promise.resolve(window.brandCursor);
  if (mounting) return mounting;
  mounting = (async () => {
    await scriptOnce('lottie_light.min.js', () => !!window.lottie);
    await scriptOnce('invokeapp-cursor.js', () => !!window.InvokeAppCursor);
    const response = await fetch(`${BASE}invokeapp-cursor.json?v=${VERSION}`);
    if (!response.ok) throw new Error('invokeapp-cursor.json');
    const animationData = await response.json();
    window.brandCursor = new window.InvokeAppCursor({
      lottie: window.lottie,
      animationData,
      size: 56,
      theme: 'auto',
    });
    return window.brandCursor;
  })().finally(() => {
    mounting = null;
  });
  return mounting;
};

const BrandCursor = () => {
  const { pathname } = useLocation();
  const brandless = isBrandless(pathname);

  useEffect(() => {
    if (!brandCursorSupported()) return undefined;
    let cancelled = false;

    const apply = () => {
      const off = brandless || isBrandCursorDisabled();
      if (off) {
        window.brandCursor?.disable();
        return;
      }
      mountCursor()
        .then((cursor) => {
          if (cancelled) return;
          // Puede haber cambiado mientras se descargaba.
          if (isBrandCursorDisabled()) cursor.disable();
          else cursor.enable();
        })
        .catch(() => {
          // Se mantiene el cursor del sistema.
        });
    };

    // Después de pintar la página: el cursor no compite con el primer render.
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(apply, { timeout: 2500 })
      : window.setTimeout(apply, 600);
    window.addEventListener(BRAND_CURSOR_EVENT, apply);
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      window.removeEventListener(BRAND_CURSOR_EVENT, apply);
    };
  }, [brandless]);

  // Al desmontar la app entera, limpieza total (listeners, estilos, SVG, timers).
  useEffect(
    () => () => {
      window.brandCursor?.destroy();
      window.brandCursor = null;
    },
    []
  );

  return null;
};

export default BrandCursor;
